import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, User, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const res = await axios.get(`${API}/posts/${slug}`);
        setPost(res.data);
      } catch (e) {
        setError("Post not found");
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-32 mb-8"></div>
            <div className="h-10 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-10 bg-gray-200 rounded w-3/4 mb-8"></div>
            <div className="h-4 bg-gray-200 rounded w-48 mb-12"></div>
            <div className="space-y-4">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-bone py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-3xl font-bold text-navy mb-4">
            Article Not Found
          </h1>
          <p className="font-body text-gray-600 mb-8">
            The article you're looking for doesn't exist or has been moved.
          </p>
          <Button
            asChild
            className="bg-navy hover:bg-gray-800 text-white rounded-full"
          >
            <Link to="/resources">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Resources
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      {/* Header */}
      <section className="bg-navy text-white noise-overlay py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/resources" 
            className="inline-flex items-center text-gray-400 hover:text-bronze mb-8 transition-colors"
          >
            <ArrowLeft className="mr-2 w-4 h-4" />
            Back to Resources
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <Tag className="w-4 h-4 text-bronze" />
            <span className="font-mono text-sm text-bronze uppercase">
              {post.category}
            </span>
          </div>

          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            {post.title}
          </h1>

          <div className="flex items-center gap-6 text-gray-400 text-sm">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              {post.author}
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              {formatDate(post.created_at)}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-bone py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div 
            className="prose max-w-none bg-white rounded-2xl p-8 md:p-12 shadow-sm"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          {/* CTA */}
          <div className="mt-12 bg-navy rounded-2xl p-8 text-center text-white">
            <h3 className="font-heading text-2xl font-bold mb-4">
              Need Help With Your Taxes?
            </h3>
            <p className="font-body text-gray-300 mb-6">
              Our team is ready to assist you with personalized tax guidance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="bg-bronze hover:bg-bronze/90 text-navy rounded-full px-8 py-4 font-medium"
              >
                <a href="#book" onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("open-booking")); }}>
                  Book Consultation
                </a>
              </Button>
              <Button
                asChild
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-navy rounded-full px-8 py-4 font-medium"
              >
                <Link to="/contact">
                  Contact Us
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
